import { useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Smartphone, Download, ArrowRight } from "lucide-react";
import { downloadFullBackup } from "@/lib/fullBackup";

let shownThisOpen = false;

const STEPS = [
  <>Tap <b>Download my data</b> below, or open <b>Menu → Settings → Export Data → Export All Data (ZIP)</b>.</>,
  <>Save the <b>stagedoor-export-….zip</b> file to your iPhone's <b>Files</b> app (Downloads folder).</>,
  <>Contact the app developer to get access to the new <b>Stage Door iOS app</b>.</>,
  <>In the iOS app, choose <b>Import</b> and pick the ZIP file. Your tours, guests, photos, shifts and time off all come with it.</>,
];

function useBackup() {
  const [busy, setBusy] = useState(false);
  const run = async () => { setBusy(true); await downloadFullBackup(); setBusy(false); };
  return [busy, run];
}

export function RetireBanner({ onShowExport }) {
  const [busy, run] = useBackup();
  return (
    <div className="rounded-xl border border-[#E0575B]/50 bg-[#9B2226]/15 px-4 py-3" data-testid="retire-banner">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <Smartphone className="mt-0.5 h-5 w-5 shrink-0 text-[#E0575B]" />
          <p className="text-sm text-zinc-200" data-testid="retire-banner-text">
            <b>Stage Door is moving to iPhone.</b> This web app is being retired — export your data and contact the app developer for access to the new iOS app.
          </p>
        </div>
        <div className="flex gap-2 pl-8 sm:pl-0">
          <Button size="sm" variant="outline" onClick={onShowExport} className="border-zinc-700" data-testid="retire-banner-howto-btn">How to</Button>
          <Button size="sm" onClick={run} disabled={busy} className="bg-[#9B2226] text-white hover:bg-[#7A1A1E]" data-testid="retire-banner-export-btn">
            <Download className="mr-1.5 h-4 w-4" /> {busy ? "Bundling…" : "Export"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function RetireDialog({ onShowExport }) {
  const [open, setOpen] = useState(() => !shownThisOpen);
  const [busy, run] = useBackup();
  const close = () => { shownThisOpen = true; setOpen(false); };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent className="max-w-md border-zinc-800 bg-zinc-950" data-testid="retire-dialog">
        <Smartphone className="h-8 w-8 text-[#E0575B]" />
        <DialogTitle className="text-xl">Stage Door is moving to iPhone</DialogTitle>
        <DialogDescription className="text-zinc-400">
          This web app is being retired. Export your data now so you can bring everything into the new Stage Door iOS app.
        </DialogDescription>
        <ol className="space-y-2.5 text-sm text-zinc-300" data-testid="retire-steps">
          {STEPS.map((s, i) => (
            <li key={i} className="flex gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#9B2226] text-xs font-700 text-white">{i + 1}</span>
              <span>{s}</span>
            </li>
          ))}
        </ol>
        <div className="flex flex-col gap-2 pt-2 sm:flex-row">
          <Button onClick={run} disabled={busy} className="flex-1 bg-[#9B2226] text-white hover:bg-[#7A1A1E]" data-testid="retire-dialog-export-btn">
            <Download className="mr-1.5 h-4 w-4" /> {busy ? "Bundling…" : "Download my data"}
          </Button>
          <Button variant="outline" onClick={() => { close(); onShowExport(); }} className="flex-1 border-zinc-700" data-testid="retire-dialog-settings-btn">
            Show me in Settings <ArrowRight className="ml-1.5 h-4 w-4" />
          </Button>
        </div>
        <button onClick={close} className="text-xs text-zinc-500 hover:text-zinc-300" data-testid="retire-dialog-later-btn">Remind me next time</button>
      </DialogContent>
    </Dialog>
  );
}
